import LoadingTextSkeleton from '@components/skeleton/LoadingTextSkeleton';
import { CellOfRow } from '@components/tables';
import { StreamType } from '@EcamModel/model/SP111RecordSetting';
import { IntervalUnit, NotifyItem, StorageFilenameType, UploadMethod } from '@EcamModel/model/SP111SnapshotSetting';
import {
    NasMountType,
    NasRecycle,
    NasStatus,
    SdCardDiskStatus,
    SdCardFullStatus,
    SdCardStatus,
    SdEncryptStatus,
} from '@EcamModel/model/SP111StorageSetting';
import { Button, Stack, TableBody, TableRow, Typography } from '@mui/material';
import { useCameraDetailContext } from '@pages/cameras/hook/CameraDetailsProvider';
import color from '@theme/Colors';
import { useEffect, useState } from 'react';
import { BiHide, BiShow } from 'react-icons/bi';
import { transformScheduleData } from 'src/helpers';
import { BaseSectionCollapse, SectionHasSubItemHeader, SectionHasSubItemProps } from '../../../ptz/milesight/components/BasicInfoMilesightCameraPTZ';
import useMileSightSP111, { SectionsCollapseSP111 } from '@pages/cameras/hook/useMilesightSP111';

const sdCardStatusMapping: { [key in SdCardStatus]: string } = {
    [SdCardStatus.UNINSERTED]: 'Uninserted',
    [SdCardStatus.INSERTED]: 'Inserted',
    [SdCardStatus.MOUNTED]: 'Mounted',
    [SdCardStatus.INSERT_AND_MOUNT]: 'Inserted and Mounted',
    [SdCardStatus.ERROR]: 'Error',
};

const sdCardDiskStatusMapping: { [key in SdCardDiskStatus]: string } = {
    [SdCardDiskStatus.UNFORMATTED]: 'Unformatted',
    [SdCardDiskStatus.NORMAL]: 'Normal',
    [SdCardDiskStatus.OFFLINE]: 'Offline',
    [SdCardDiskStatus.FORMATTING]: 'Formatting',
    [SdCardDiskStatus.BAD]: 'Bad',
    [SdCardDiskStatus.LOADING]: 'Loading',
    [SdCardDiskStatus.NONE]: 'None',
};

const sdCardFullStatusMapping: { [key in SdCardFullStatus]: string } = {
    [SdCardFullStatus.NO]: 'No',
    [SdCardFullStatus.YES]: 'Yes',
};

const sdEncryptStatusMapping: { [key in SdEncryptStatus]: string } = {
    [SdEncryptStatus.UNENCRYPTED]: 'Unencrypted',
    [SdEncryptStatus.ENCRYPTED_SUCCESS]: 'Encrypted Success',
    [SdEncryptStatus.ENCRYPTED_FAILED]: 'Encrypted Failed',
};

const nasRecycleMapping: { [key in NasRecycle]: string } = {
    [NasRecycle.NO]: 'No',
    [NasRecycle.YES]: 'Yes',
};

const nasMountTypeMapping: { [key in NasMountType]: string } = {
    [NasMountType.NFS]: 'NFS',
    [NasMountType.SMB_CIFS]: 'SMB/CIFS',
};

const nasStatusMapping: { [key in NasStatus]: string } = {
    [NasStatus.UNFORMATTED]: 'Unformatted',
    [NasStatus.NORMAL]: 'Normal',
    [NasStatus.OFFLINE]: 'Offline',
    [NasStatus.FORMATTING]: 'Formatting',
};

const streamTypeMapping: { [key in StreamType]: string } = {
    [StreamType.PrimaryStream]: 'Primary Stream',
    [StreamType.SecondaryStream]: 'Secondary Stream',
    [StreamType.TertiaryStream]: 'Tertiary Stream',
    [StreamType.FourthStream]: 'Fourth Stream',
    [StreamType.FifthStream]: 'Fifth Stream',
};

const intervalUnitMapping: { [key in IntervalUnit]: string } = {
    [IntervalUnit.SECONDS]: 's',
    [IntervalUnit.MINUTES]: 'm',
    [IntervalUnit.HOURS]: 'h',
    [IntervalUnit.DAYS]: 'd',
};

const storageFilenameTypeMapping: { [key in StorageFilenameType]: string } = {
    [StorageFilenameType.TYPE_0]: 'Type 0',
};

const uploadMethodMapping: { [key in UploadMethod]: string } = {
    [UploadMethod.FTP]: 'FTP',
    [UploadMethod.SMTP]: 'SMTP',
    [UploadMethod.HTTP]: 'HTTP',
};

const tableHeaders: string[] = ['No.', 'Server address', 'Directory', 'Mounting type', 'Total', 'Free'];

type Props = {
    isOpenCollapse: { [key in SectionsCollapseSP111]: boolean };
    handleToggleCollapse: (section: SectionsCollapseSP111) => void;
};

export default function StorageMilesightsCameraSP111(props: Props) {
    const { camera, filteredCamera } = useCameraDetailContext();
    const { isOpenCollapse, handleToggleCollapse } = props;
    const {
        isLoadingSP111StorageSetting,
        isLoadingSP111SnapShotSetting,
        isLoadingSP111RecordSetting,
        SP111StorageSetting,
        SP111SnapShotSetting,
        SP111RecordSetting,
    } = useMileSightSP111({
        filteredCamera,
        cameraSP111: camera?.SP111CameraId!,
    });

    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [buttonSelected, setButtonSelected] = useState<number>(1);
    const [snapshotInfo, setSnapshotInfo] = useState<NotifyItem>({} as NotifyItem);

    const handleEdit = () => {
        console.log('Edit button clicked');
    };

    useEffect(() => {
        const initialData = SP111SnapShotSetting?.NotifyList?.[0];
        if (initialData) {
            setSnapshotInfo(initialData);
            setButtonSelected(initialData.Index + 1);
        }
    }, [SP111SnapShotSetting]);

    const handleButtonClick = (index: number) => {
        setButtonSelected(index);

        const selectedData = SP111SnapShotSetting?.NotifyList?.find((item) => item.Index + 1 === index);
        if (selectedData) {
            setSnapshotInfo((prev) => ({
                ...prev,
                Enable: selectedData.Enable,
                User: selectedData.User,
                Password: selectedData.Password,
                Url: selectedData.Url,
            }));
        }
    };

    const sectionsStorageManagement: SectionHasSubItemProps[] = [
        {
            title: 'SD card',
            data: [
                {
                    label: 'SD card:',
                    value: (
                        <LoadingTextSkeleton
                            conditions={isLoadingSP111StorageSetting}
                            value={`${SP111StorageSetting?.SdcardFreeSize!}M / ${SP111StorageSetting?.SdcardUseSize!}M`}
                            loadingStyle={{
                                width: '50%',
                            }}
                        />
                    ),
                },
            ],
        },
        {
            title: 'SD Card Encryption',
            data: [
                {
                    label: 'Encryption status:',
                    value: (
                        <LoadingTextSkeleton
                            conditions={isLoadingSP111StorageSetting}
                            value={sdEncryptStatusMapping[SP111StorageSetting?.SdEncryptStatus!]}
                            loadingStyle={{
                                width: '50%',
                            }}
                        />
                    ),
                },
                // {
                //     label: 'Authentication status:',
                //     value: <>-</>,
                // },
                // { label: 'Username:', value: 'admin' },
                // { label: 'Password:', value: 'ms123456' },
            ],
        },
        {
            title: 'NAS',
            tableHeaders: tableHeaders,
            items: SP111StorageSetting?.NasList,
            tableBody: (
                <TableBody>
                    {SP111StorageSetting?.NasList?.map((row, index) => {
                        return (
                            <TableRow key={index}>
                                <CellOfRow value={`${index + 1}`} align="left" />
                                <CellOfRow value={row.NasAddress} align="left" />
                                <CellOfRow value={row.NasFilePath} align="left" />
                                <CellOfRow value={nasMountTypeMapping[row.NasMountType]} align="left" />
                                <CellOfRow value={row.NasTotalSize} align="left" />
                                <CellOfRow value={row.NasFreeSize} align="left" />
                            </TableRow>
                        );
                    })}
                </TableBody>
            ),
        },
    ];

    const sectionsRecordSettings: SectionHasSubItemProps[] = [
        {
            title: 'Storage settings',
            data: [
                {
                    label: 'Enable recycle storage:',
                    value: (
                        <LoadingTextSkeleton
                            conditions={isLoadingSP111RecordSetting}
                            value={SP111RecordSetting?.RecycleStorageEnable! ? 'Yes' : 'No'}
                            loadingStyle={{
                                width: '50%',
                            }}
                        />
                    ),
                },
                {
                    label: 'Stream type:',
                    value: (
                        <LoadingTextSkeleton
                            conditions={isLoadingSP111RecordSetting}
                            value={`${
                                streamTypeMapping[SP111RecordSetting?.StreamType!]
                            } (Please enable the corresponding stream video)`}
                            loadingStyle={{
                                width: '50%',
                            }}
                        />
                    ),
                },
                {
                    label: 'Pre-record:',
                    value: (
                        <LoadingTextSkeleton
                            conditions={isLoadingSP111RecordSetting}
                            value={`${SP111RecordSetting?.PreRecordTime!}s`}
                            loadingStyle={{
                                width: '50%',
                            }}
                        />
                    ),
                },
            ],
        },
        {
            title: 'Schedule settings',
            data: transformScheduleData(SP111RecordSetting?.ScheduleList!, (dayName, schedule) => ({
                label: `${dayName}:`,
                value: schedule,
            })),
        },
    ];

    const sectionsSnapshotSettings: SectionHasSubItemProps[] = [
        {
            title: 'Snapshot settings',
            data: [
                {
                    label: 'Enable timing snapshot:',
                    value: (
                        <LoadingTextSkeleton
                            conditions={isLoadingSP111SnapShotSetting}
                            value={SP111SnapShotSetting?.TimingSnapshotEnable ? 'Yes' : 'No'}
                            loadingStyle={{
                                width: '50%',
                            }}
                        />
                    ),
                },
                {
                    label: 'Interval:',
                    value: (
                        <LoadingTextSkeleton
                            conditions={isLoadingSP111SnapShotSetting}
                            value={`${SP111SnapShotSetting?.IntervalValue}${
                                intervalUnitMapping[SP111SnapShotSetting?.IntervalUnit!]
                            }`}
                            loadingStyle={{
                                width: '50%',
                            }}
                        />
                    ),
                },
                {
                    label: 'Save to storage:',
                    value: (
                        <LoadingTextSkeleton
                            conditions={isLoadingSP111SnapShotSetting}
                            value={SP111SnapShotSetting?.SaveIntoStorage ? 'Yes' : 'No'}
                            loadingStyle={{
                                width: '50%',
                            }}
                        />
                    ),
                },
                {
                    label: 'Upload via FTP:',
                    value: (
                        <LoadingTextSkeleton
                            conditions={isLoadingSP111SnapShotSetting}
                            value={SP111SnapShotSetting?.UploadViaFtp ? 'Yes' : 'No'}
                            loadingStyle={{
                                width: '50%',
                            }}
                        />
                    ),
                },
                {
                    label: 'Upload via Email:',
                    value: (
                        <LoadingTextSkeleton
                            conditions={isLoadingSP111SnapShotSetting}
                            value={SP111SnapShotSetting?.UploadViaSmtp ? 'Yes' : 'No'}
                            loadingStyle={{
                                width: '50%',
                            }}
                        />
                    ),
                },
                {
                    label: 'HTTP post:',
                    value: (
                        <LoadingTextSkeleton
                            conditions={isLoadingSP111SnapShotSetting}
                            value={SP111SnapShotSetting?.UploadViaHttp ? 'Yes' : 'No'}
                            loadingStyle={{
                                width: '50%',
                            }}
                        />
                    ),
                },
                {
                    label: 'URL:',
                    value: (
                        <Stack direction={'row'} spacing={2}>
                            {SP111SnapShotSetting?.NotifyList?.map((value) => (
                                <Button
                                    key={value.Index}
                                    variant="contained"
                                    size="small"
                                    onClick={() => handleButtonClick(value.Index + 1)}
                                    sx={{
                                        backgroundColor: 'transparent',
                                        border:
                                            buttonSelected === value.Index + 1
                                                ? `1px solid ${color.success}`
                                                : `1px solid ${color.grey200}`,
                                        minWidth: '50px',
                                        '&:hover': {
                                            backgroundColor:
                                                buttonSelected === value.Index + 1 ? '#E5F5ED' : color.grey200,
                                            borderColor:
                                                buttonSelected === value.Index + 1 ? color.success : color.grey200,
                                        },
                                    }}
                                >
                                    <Typography textAlign={'center'} fontSize={12}>
                                        {value.Index + 1}
                                    </Typography>
                                </Button>
                            ))}
                        </Stack>
                    ),
                },
                {
                    label: '',
                    value: (
                        <LoadingTextSkeleton
                            conditions={snapshotInfo.Password === undefined || isLoadingSP111SnapShotSetting}
                            value={snapshotInfo.Url}
                            loadingStyle={{
                                width: '50%',
                            }}
                        />
                    ),
                },
                {
                    label: 'Enable:',
                    value: (
                        <LoadingTextSkeleton
                            conditions={snapshotInfo.Password === undefined || isLoadingSP111SnapShotSetting}
                            value={snapshotInfo.Enable ? 'Yes' : 'No'}
                            loadingStyle={{
                                width: '50%',
                            }}
                        />
                    ),
                },
                {
                    label: 'User name:',
                    value: (
                        <LoadingTextSkeleton
                            conditions={snapshotInfo.Password === undefined || isLoadingSP111SnapShotSetting}
                            value={snapshotInfo.User}
                            loadingStyle={{
                                width: '50%',
                            }}
                        />
                    ),
                },
                {
                    label: 'Password:',
                    value: (
                        <Stack direction={'row'} justifyContent={'space-between'} width={'100%'} alignItems={'center'}>
                            <LoadingTextSkeleton
                                conditions={snapshotInfo.Password === undefined || isLoadingSP111SnapShotSetting}
                                value={showPassword ? snapshotInfo.Password : snapshotInfo.Password?.replace(/./g, '*')}
                                loadingStyle={{
                                    width: '50%',
                                }}
                            />

                            {showPassword ? (
                                <BiHide
                                    fontSize={18}
                                    color="#85858A"
                                    cursor="pointer"
                                    onClick={() => setShowPassword(false)}
                                />
                            ) : (
                                <BiShow
                                    fontSize={18}
                                    color="#85858A"
                                    cursor="pointer"
                                    onClick={() => setShowPassword(true)}
                                />
                            )}
                        </Stack>
                    ),
                },
            ],
        },
        {
            title: 'Schedule settings',
            data: transformScheduleData(SP111SnapShotSetting?.ScheduleList!, (dayName, schedule) => ({
                label: `${dayName}:`,
                value: schedule,
            })),
        },
    ];

    return (
        <Stack id="storageManagement" border={'1px solid #E4E7EC'} borderRadius={2} flex={1}>
            <SectionHasSubItemHeader id="" title="Storage" onEdit={handleEdit} />

            <BaseSectionCollapse
                title="Storage management"
                isOpen={isOpenCollapse.storageManagement}
                onToggle={() => {
                    handleToggleCollapse(SectionsCollapseSP111.StorageManagement);
                }}
                sections={sectionsStorageManagement}
            />

            <BaseSectionCollapse
                title="Record settings"
                id="recordSettings"
                isOpen={isOpenCollapse.recordSettings}
                onToggle={() => {
                    handleToggleCollapse(SectionsCollapseSP111.RecordSettings);
                }}
                sections={sectionsRecordSettings}
            />

            <BaseSectionCollapse
                title="Snapshot settings"
                id="snapshotSettings"
                isOpen={isOpenCollapse.snapshotSettings}
                onToggle={() => {
                    handleToggleCollapse(SectionsCollapseSP111.SnapshotSettings);
                }}
                sections={sectionsSnapshotSettings}
            />
        </Stack>
    );
}
